/**
 * ArCashFlowTab — Flujo de caja proyectado (ingresos + egresos).
 */
import { useState } from 'react'
import { useArCashFlow } from '../hooks/useFinance'
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { DataTable } from '@/components/ds/DataTable'

export default function ArCashFlowTab() {
  const [weeks, setWeeks] = useState(12)
  const { data, isLoading } = useArCashFlow(weeks)
  const { data: locConfig } = useLocalizationConfig()
  const locale = useTenantLocale()
  const currency = locConfig?.currency || 'CLP'
  const isCLP = currency === 'CLP'
  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    const decimals = isCLP ? 0 : (n % 1 !== 0 ? 2 : 0)
    return `$${formatNumber(n, locale, decimals)}`
  }

  if (isLoading) return <p className="text-center text-sm text-content-subtle py-8">Cargando...</p>
  if (!data) return null

  const weeksData = data.weeks || []

  return (
    <>
      {/* Filters + currency */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="ar-cashflow-weeks" className="text-xs font-medium text-content-muted">Horizonte</label>
          <select
            id="ar-cashflow-weeks"
            value={weeks}
            onChange={e => setWeeks(Number(e.target.value))}
            className="px-2.5 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
          >
            <option value={4}>4 semanas</option>
            <option value={8}>8 semanas</option>
            <option value={12}>12 semanas</option>
            <option value={24}>24 semanas</option>
            <option value={52}>52 semanas</option>
          </select>
        </div>
        <p className="text-xs text-content-muted ml-auto">
          Montos en <span className="font-mono font-semibold text-content">{currency}</span>
          {locConfig?.currency_display && <span> ({locConfig.currency_display})</span>}
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-success-surface border border-success-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-success-content-soft text-xs font-medium mb-1">
            <TrendingUp size={14} /> Ingresos esperados
          </div>
          <p className="text-xl font-bold text-success-content">{fmt(data.total_inflow)}</p>
        </div>
        <div className="bg-danger-surface border border-danger-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-danger-content-soft text-xs font-medium mb-1">
            <TrendingDown size={14} /> Egresos esperados
          </div>
          <p className="text-xl font-bold text-danger-content">{fmt(data.total_outflow)}</p>
        </div>
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-primary text-xs font-medium mb-1">
            <DollarSign size={14} /> Flujo neto
          </div>
          <p className={`text-xl font-bold ${parseFloat(data.total_net) >= 0 ? 'text-success-content' : 'text-danger-content'}`}>
            {fmt(data.total_net)}
          </p>
        </div>
      </div>

      {/* Weekly breakdown */}
      <DataTable
        columns={[
          { key: 'semana', header: 'Semana', render: (w: any) => `${w.week_start} — ${w.week_end}` },
          {
            key: 'inflow', header: 'Ingresos', numeric: true,
            render: (w: any) => (
              <span className="text-success-content-soft font-medium">
                {parseFloat(w.inflow) > 0 ? fmt(w.inflow) : '—'}
              </span>
            ),
          },
          {
            key: 'outflow', header: 'Egresos', numeric: true,
            render: (w: any) => (
              <span className="text-danger-content-soft font-medium">
                {parseFloat(w.outflow) > 0 ? fmt(w.outflow) : '—'}
              </span>
            ),
          },
          {
            key: 'net', header: 'Neto', numeric: true,
            render: (w: any) => {
              const net = parseFloat(w.net)
              return (
                <span className={`font-bold ${net >= 0 ? 'text-success-content' : 'text-danger-content'}`}>
                  {fmt(net)}
                </span>
              )
            },
          },
        ]}
        rows={weeksData}
        rowKey={(_: any, i: number) => i}
        minWidth={560}
        caption="Proyección semanal de flujo por cobrar"
      />
    </>
  )
}
